import { useEffect } from "react";

import { AIServerRoleType } from "components/server-types.ts";
import { useAppDispatch } from "store/index";
import { aiStateActions } from "store/slices/ai";

// Stable ids for client-injected AI context (`useAIContext`). Server-built context uses separate ids
// (see server `ContextPartId`); keep this list in sync with actual `useAIContext(...)` call sites only.
export type AIContextKeyType = "course_progress_demo";

/**
 * This hook ensures that the passed data is available in the AI context,
 * both for chat and for AI actions, for as long as the component is mounted.
 * The context will be removed again when the component unmounts.
 * Clearing the chat will _not_ remove the context.
 * Using the same key twice will overwrite the previous value, allowing
 * children to override the parent's context.
 *
 * Be sure to memoize the data being passed in.
 *
 * To see active context items, use the AIAssistantDebugger
 * visible in the AIChatPanel for MP admins.
 */
export const useAIContext = (
  key: AIContextKeyType,
  data: string | null | false | undefined,
  role: AIServerRoleType,
) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const setContext = (truthyData: string) => {
      dispatch(
        aiStateActions.setAiChatData({
          id: key,
          role,
          text: truthyData,
          type: "context",
        }),
      );
    };
    const removeContext = () => {
      dispatch(aiStateActions.removeAiChatData(key));
    };
    if (data) {
      setContext(data);
    } else {
      removeContext();
    }
    // TODO: This results in a lot of deleting/re-adding of state,
    // which is fine, but messes up the ordering in the debugging menu.
    return removeContext;
  }, [dispatch, data, key, role]);
};
